import { useCallback, useMemo, useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Stack } from '@mui/material'
import {
  LicensePeriodType,
  LicensingPeriod
} from '@/components/inputs/LicensingPeriods/dde/LicensingPeriod.dde'
import { Scrollbar } from '@/components/Scrollbar'

type Props = {
  defaultValue?: LicensePeriodType[]
  onChange?: (periods: LicensePeriodType[], isValid: boolean) => void
  height?: number | string
}

type PeriodWithId = Omit<LicensePeriodType, 'id'> & { id: string }

const isPeriodValid = (period: PeriodWithId): boolean => {
  return !!(
    period.year &&
    period.startDate &&
    period.endDate &&
    (period.numOfBookedEvents ?? 0) >= 0
  )
}

export const LicensingPeriods = ({ defaultValue = [], onChange, height = 400 }: Props) => {
  const [periods, setPeriods] = useState<PeriodWithId[]>(() =>
    defaultValue.map((period, index) => ({
      ...period,
      id: period.id ? period.id.toString() : `new-period-${Date.now()}-${index}`
    }))
  )

  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showValidationErrors, setShowValidationErrors] = useState<Set<string>>(new Set())
  const [addButtonDisabled, setAddButtonDisabled] = useState(false)

  const notifyParent = useCallback(
    (newPeriods: PeriodWithId[]) => {
      const periodsForParent = newPeriods.map((period) => {
        const { id, ...periodData } = period
        return {
          ...periodData,
          id: id.startsWith('new-period-') ? null : parseInt(id, 10)
        }
      })
      const allValid = newPeriods.every((period) => isPeriodValid(period))
      onChange?.(periodsForParent as LicensePeriodType[], allValid)
    },
    [onChange]
  )

  const handleAddPeriod = useCallback(() => {
    const hasInvalidPeriods = periods.some((period) => !isPeriodValid(period))

    if (hasInvalidPeriods) {
      const invalidPeriods = periods.filter((period) => !isPeriodValid(period))
      const invalidPeriodIds = new Set(invalidPeriods.map((period) => period.id))
      setShowValidationErrors(invalidPeriodIds)
      setAddButtonDisabled(true)

      if (invalidPeriods.length > 0) {
        setExpandedId(invalidPeriods[0].id)
      }

      return
    }

    const newPeriod: PeriodWithId = {
      year: '',
      startDate: '',
      endDate: '',
      numOfBookedEvents: 0,
      numOfConsumedEvents: 0,
      numOfEvents: 0,
      unlimited: true,
      suspended: false,
      id: `new-period-${Date.now()}`
    }

    const newPeriods = [newPeriod, ...periods]
    setPeriods(newPeriods)
    setExpandedId(newPeriod.id)
    notifyParent(newPeriods)
  }, [periods, notifyParent])

  const handleDeletePeriod = useCallback(
    (index: number) => {
      const periodToDelete = periods[index]
      const newPeriods = periods.filter((_, i) => i !== index)
      setPeriods(newPeriods)

      if (expandedId === periodToDelete.id) {
        setExpandedId(null)
      }

      setShowValidationErrors((prev) => {
        const newSet = new Set(prev)
        newSet.delete(periodToDelete.id)
        return newSet
      })

      const allRemainingPeriodsValid = newPeriods.every((period) => isPeriodValid(period))
      if (allRemainingPeriodsValid && addButtonDisabled) {
        setAddButtonDisabled(false)
      }

      notifyParent(newPeriods)
    },
    [periods, expandedId, notifyParent, addButtonDisabled]
  )

  const handlePeriodChange = useCallback(
    (index: number, updatedPeriod: Partial<PeriodWithId>) => {
      const newPeriods = [...periods]
      newPeriods[index] = { ...newPeriods[index], ...updatedPeriod }
      setPeriods(newPeriods)

      const updatedFullPeriod = newPeriods[index]
      if (isPeriodValid(updatedFullPeriod)) {
        setShowValidationErrors((prev) => {
          const newSet = new Set(prev)
          newSet.delete(updatedFullPeriod.id)
          return newSet
        })
      }

      const allPeriodsValid = newPeriods.every((period) => isPeriodValid(period))
      if (allPeriodsValid && addButtonDisabled) {
        setAddButtonDisabled(false)
      }

      notifyParent(newPeriods)
    },
    [periods, notifyParent, addButtonDisabled]
  )

  const handleExpandedChange = useCallback((periodId: string, expanded: boolean) => {
    setExpandedId(expanded ? periodId : null)
  }, [])

  const usedYearsByPeriod = useMemo(() => {
    const usedYearsMap = new Map<string, Set<string>>()

    periods.forEach((period) => {
      const usedYears = new Set(
        periods.filter((p) => p.id !== period.id && p.year).map((p) => p.year)
      )
      usedYearsMap.set(period.id, usedYears)
    })

    return usedYearsMap
  }, [periods])

  return (
    <Box
      sx={{
        height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        mr: -1
      }}
    >
      <Scrollbar style={{ height: '100%' }}>
        <Stack spacing={1} sx={{ pr: 1 }}>
          {periods.map((period, index) => {
            const isValid = isPeriodValid(period)
            const shouldShowErrors = showValidationErrors.has(period.id)
            const usedYearsForThisPeriod = usedYearsByPeriod.get(period.id) || new Set()

            return (
              <LicensingPeriod
                key={period.id}
                value={{
                  ...period,
                  id: period.id.startsWith('new-period-') ? undefined : parseInt(period.id, 10)
                }}
                expanded={expandedId === period.id}
                usedYears={usedYearsForThisPeriod}
                onExpandedChange={(expanded) => handleExpandedChange(period.id, expanded)}
                onDelete={() => handleDeletePeriod(index)}
                onChange={(updatedPeriod) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { id, ...rest } = updatedPeriod
                  handlePeriodChange(index, rest)
                }}
                hasError={
                  !isValid && shouldShowErrors ? 'Please fill in all required fields' : false
                }
              />
            )
          })}

          {periods.length === 0 && (
            <Box
              sx={{
                p: 1,
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              No periods yet
            </Box>
          )}

          <Box
            sx={{
              position: 'sticky',
              bottom: '.75rem',
              pt: 1.75,
              mt: 'auto',
              zIndex: 1
            }}
          >
            <Button
              variant="dashed"
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleAddPeriod}
              disabled={addButtonDisabled}
            >
              Add Period
            </Button>
          </Box>
        </Stack>
      </Scrollbar>
    </Box>
  )
}
